import { type FC, useState } from 'react';
import { motion } from 'framer-motion';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { MdCheckCircle } from 'react-icons/md';
import type { Task } from '../types';
import { useTheme } from '../hooks/useTheme';

interface TaskTimelineProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

const TaskTimeline: FC<TaskTimelineProps> = ({ tasks, onTaskClick }) => {
  const { theme } = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 }); // Sunday

  // Generate 7 days for the week
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Get tasks for a specific day
  const getTasksForDay = (date: Date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      return isSameDay(parseISO(task.due_date), date);
    });
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return theme === 'dark' ? '#FF0080' : '#ec4899';
      case 'medium':
        return 'var(--color-theme)';
      case 'low':
        return theme === 'dark' ? '#00FF00' : '#22c55e';
      default:
        return theme === 'dark' ? '#6b7280' : '#9ca3af';
    }
  };

  return (
    <div className="space-y-4">
      {/* Week Navigator */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setSelectedDate(addDays(selectedDate, -7))}
          className="px-4 py-2 rounded-lg font-semibold transition-all"
          style={{
            backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            color: theme === 'dark' ? '#e5e7eb' : '#374151'
          }}
        >
          ← Previous Week
        </button>
        <h3 className="text-lg font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
          {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </h3>
        <button
          onClick={() => setSelectedDate(addDays(selectedDate, 7))}
          className="px-4 py-2 rounded-lg font-semibold transition-all"
          style={{
            backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            color: theme === 'dark' ? '#e5e7eb' : '#374151'
          }}
        >
          Next Week →
        </button>
      </div>

      {/* Timeline Grid */}
      <div className="space-y-2">
        {weekDays.map((day, dayIndex) => {
          const dayTasks = getTasksForDay(day);
          const isToday = isSameDay(day, new Date());

          return (
            <motion.div
              key={dayIndex}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: dayIndex * 0.05 }}
              className="rounded-lg border-2 overflow-hidden"
              style={{
                backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                borderColor: isToday ? 'var(--color-theme)' : (theme === 'dark' ? '#374151' : '#e5e7eb')
              }}
            >
              {/* Day Header */}
              <div 
                className="px-4 py-2 border-b-2 flex items-center justify-between"
                style={{
                  backgroundColor: isToday 
                    ? (theme === 'dark' ? 'rgba(var(--accent-color-rgb), 0.1)' : 'rgba(var(--accent-color-rgb), 0.05)')
                    : (theme === 'dark' ? '#111827' : '#f9fafb'),
                  borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                }}
              >
                <div>
                  <span className="font-bold text-lg" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                    {format(day, 'EEEE')}
                  </span>
                  <span className="ml-2 text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                    {format(day, 'MMM d')}
                  </span>
                  {isToday && (
                    <span 
                      className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: 'var(--color-theme)',
                        color: '#ffffff'
                      }}
                    >
                      Today
                    </span>
                  )}
                </div>
                <span 
                  className="text-sm font-semibold"
                  style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                >
                  {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Tasks for the day */}
              <div className="p-2">
                {dayTasks.length === 0 ? (
                  <div 
                    className="text-center py-6 text-sm"
                    style={{ color: theme === 'dark' ? '#6b7280' : '#9ca3af' }}
                  >
                    No tasks scheduled
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayTasks.map((task) => (
                      <motion.div
                        key={task.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => onTaskClick?.(task)}
                        className="p-3 rounded-lg border-l-4 cursor-pointer transition-all"
                        style={{
                          backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb',
                          borderLeftColor: getPriorityColor(task.priority)
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {task.status === 'completed' && (
                                <MdCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              )}
                              <h4 
                                className={`font-semibold text-sm truncate ${task.status === 'completed' ? 'line-through' : ''}`}
                                style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                              >
                                {task.title}
                              </h4>
                            </div>
                            {task.description && (
                              <p 
                                className="text-xs line-clamp-1"
                                style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                              >
                                {task.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full capitalize"
                                style={{
                                  backgroundColor: task.status === 'completed' 
                                    ? (theme === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)')
                                    : task.status === 'in-progress'
                                    ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
                                    : (theme === 'dark' ? 'rgba(156, 163, 175, 0.2)' : 'rgba(156, 163, 175, 0.1)'),
                                  color: task.status === 'completed'
                                    ? '#22c55e'
                                    : task.status === 'in-progress'
                                    ? '#3b82f6'
                                    : '#6b7280'
                                }}
                              >
                                {task.status.replace('-', ' ')}
                              </span>
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full capitalize"
                                style={{
                                  backgroundColor: theme === 'dark' ? 'rgba(var(--accent-color-rgb), 0.2)' : 'rgba(var(--accent-color-rgb), 0.1)',
                                  color: getPriorityColor(task.priority)
                                }}
                              >
                                {task.priority}
                              </span>
                              {task.start_time && (
                                <span 
                                  className="text-xs"
                                  style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                                >
                                  🕐 {format(parseISO(task.start_time), 'h:mm a')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default TaskTimeline;
